<script setup lang="ts">
import { Ort, OrtCollection } from '@/models/Ort'
import { onMounted, ref } from 'vue'
import { formRules } from '@/helpers/validators'
import { debounce } from 'vuetify/lib/util/helpers.mjs'

/**
 * PLZ-Ort-Select
 *
 * loads Orte from /lookup/ort,
 * emits the selected
 */

const modelValue = defineModel<Ort | null>('modelValue')
const ortId = defineModel<number | null>('ortId')

const {
    ort,
    plz,
    dense = false,
    rules = [],
    readOnly = false,
} = defineProps<{
    ort?: string | null
    plz?: string | null
    rules?: ((value: string) => string | boolean)[]
    dense?: boolean
    readOnly?: boolean
}>()

const orteCollection = new OrtCollection()

const loading = ref(false)

const emit = defineEmits<{
    (e: 'update:plz', plz: string | null): void
    (e: 'update:ort', ort: string | null): void
}>()

onMounted(async () => {
    if (ort && plz) {
        orteCollection.setQueryParams({ ort, plz })
        await orteCollection.query()
        modelValue.value = orteCollection.first()
    } else if (ortId.value) {
        orteCollection.setQueryParams({ id: ortId.value })
        await orteCollection.query()
        modelValue.value = orteCollection.first()
    }
})

async function searchPlzOrt(value: string) {
    if (!value || value.length < 3 || loading.value) {
        return
    }
    debounce(loadPlzOrte, 300)(value)
}

function onPlzOrtSelect(value: Ort) {
    ortId.value = value.id
    if (value && value.plz) {
        emit('update:plz', value.plz.trim())
        emit('update:ort', value.ort?.trim() || null)
    }
}

async function loadPlzOrte(query: string | null = null) {
    loading.value = true
    orteCollection.removeQueryParam('ort')
    orteCollection.removeQueryParam('plz')
    await orteCollection.query({ query })
    loading.value = false
}
</script>

<template>
    <div class="text-subtitle-1 text-medium-emphasis">
        {{ $t('plz_ort') + (rules.includes(formRules.required) ? ' *' : '') }}
    </div>
    <v-autocomplete
        style="margin-left: -1rem"
        autocomplete="new-password"
        :dense="dense"
        v-model:model-value="modelValue"
        :items="orteCollection ? orteCollection.getModels() : []"
        :loading="loading"
        :readonly="readOnly"
        item-title="plz_ort"
        item-value="id"
        clear-icon="fas fa-times"
        return-object
        hide-no-data
        hide-selected
        :rules="rules"
        @update:search="searchPlzOrt"
        @update:modelValue="onPlzOrtSelect"
    >
    </v-autocomplete>
</template>
